import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { StockRegion, StockRegionRow } from "../types";
import { stockRegionRowToRegion } from "../types";
import { uuid } from "../utils/uuid";

export function useStockRegions(ownerId?: string) {
  const [regions, setRegions] = useState<StockRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!ownerId) {
      setRegions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from("stock_regions")
        .select("*")
        .eq("owner_id", ownerId)
        .order("name", { ascending: true });
      if (e) throw e;
      const rows = (data ?? []) as StockRegionRow[];
      setRegions(rows.map(stockRegionRowToRegion));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement régions");
      setRegions([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const add = useCallback(
    async (name: string) => {
      if (!ownerId) return { data: null, error: "Non autorisé" };
      try {
        const id = uuid();
        const insertData = {
          id,
          owner_id: ownerId,
          name: name.trim(),
        };
        const { data, error: e } = await supabase
          .from("stock_regions")
          .insert(insertData)
          .select()
          .single();
        if (e) {
          const detail = (e as { message?: string; details?: string; hint?: string }).details ?? (e as { message?: string }).message;
          throw new Error(detail || e.message || "Erreur ajout région");
        }
        const region = stockRegionRowToRegion(data as StockRegionRow);
        setRegions((prev) => [...prev, region].sort((a, b) => a.name.localeCompare(b.name)));
        return { data: region, error: null };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur ajout région";
        return { data: null, error: msg };
      }
    },
    [ownerId]
  );

  const update = useCallback(
    async (id: string, name: string) => {
      if (!ownerId) return { data: null, error: "Non autorisé" };
      try {
        const { data, error: e } = await supabase
          .from("stock_regions")
          .update({ name: name.trim(), updated_at: new Date().toISOString() })
          .eq("id", id)
          .eq("owner_id", ownerId)
          .select()
          .single();
        if (e) throw e;
        const region = stockRegionRowToRegion(data as StockRegionRow);
        setRegions((prev) => prev.map((r) => (r.id === id ? region : r)));
        return { data: region, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur mise à jour région",
        };
      }
    },
    [ownerId]
  );

  const remove = useCallback(
    async (id: string) => {
      if (!ownerId) return { error: "Non autorisé" };
      try {
        const { error: e } = await supabase
          .from("stock_regions")
          .delete()
          .eq("id", id)
          .eq("owner_id", ownerId);
        if (e) throw e;
        setRegions((prev) => prev.filter((r) => r.id !== id));
        return { error: null };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Erreur suppression région",
        };
      }
    },
    [ownerId]
  );

  return { regions, loading, error, refetch: fetchAll, add, update, remove };
}
